import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { ASSETS } from "@/constants/site";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog/posts")
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <SEO
        title="Blog"
        description="Conteúdo veterinário sobre diagnóstico por imagem, ressonância, tomografia, ultrassom e cuidados com cães e gatos em Belo Horizonte."
        path="/blog"
      />

      <section className="bg-white section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-brand-600 font-semibold text-sm uppercase tracking-wider mb-3">Blog</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Conteúdo veterinário para tutores e profissionais
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Informações claras e revisadas por médicos veterinários sobre exames por imagem, preparo de pacientes e cuidados com pets.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-slate-500">Carregando artigos...</div>
          ) : posts.length === 0 ? (
            <div className="text-slate-500">Nenhum artigo publicado ainda.</div>
          ) : (
            <div data-testid={TESTIDS.blogList} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p, i) => (
                <motion.article
                  key={p.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: (i % 6) * 0.06 }}
                  data-testid={TESTIDS.blogCard}
                  className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-brand-300 hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-300"
                >
                  <Link to={`/blog/${p.slug}`}>
                    <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                      <img src={p.cover_image || ASSETS.blogGeneric} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                        <CalendarDays className="w-3.5 h-3.5" />
                        {new Date(p.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}
                      </div>
                      <h3 className="font-heading font-semibold text-lg text-slate-900 leading-snug">{p.title}</h3>
                      <p className="mt-2 text-sm text-slate-600 line-clamp-3">{p.excerpt}</p>
                      <div className="mt-4 inline-flex items-center gap-1.5 text-brand-600 font-semibold text-sm">
                        Ler artigo <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
